import { useEffect, useRef, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import { useNavigate, useParams } from "react-router-dom";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import { faEdit, faPaperPlane, faTrash } from "@fortawesome/free-solid-svg-icons";
import { ProviderNoteApi, ProviderNoteDataType } from "@/api/ProviderNoteApi";
import { formatMoneyValue } from "@utils/Currency";
import { showAlert } from "@utils/Alert";
import styles from "./style.module.scss";
import { PROVIDER_NOTE_TYPES } from "@utils/Constants";
import { ProviderInvoiceApi, ProviderInvoiceDataType } from "@/api/ProviderInvoiceApi";
import { Col, Container, Row } from "reactstrap";
import { ProviderInvoiceFormDataKeys } from "@/generated/ProviderInvoiceFormDataKeys";
import { ProviderInvoiceRelatedAttributes } from "@/formTypes/ProviderInvoiceDataType";
import { formatDate } from "@utils/Api";
import LoadingScreen from "@components/content/LoadingScreen";
import { FetchResponse } from "@/api/ApiClient";

const ProviderNoteIndex = () => {
  const navigate = useNavigate();
  const params = useParams();
  const modelRef = useRef<ModelIndexHandle>(null);
  const { id: providerInvoiceId } = params;
  const [providerInvoice, setProviderInvoice] = useState<ProviderInvoiceDataType>({})
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (providerInvoiceId) {
      const run = async (providerInvoiceId: string) => {
        try {
          const invoices = await ProviderInvoiceApi.get(providerInvoiceId, {
            selectionSet: [
              ...ProviderInvoiceFormDataKeys.filter(k => !ProviderInvoiceRelatedAttributes.includes(k)),
              "currency.*",
              "provider.*",
              "providerNotes.*",
              "providerNotes.currency.*"
            ]
          });
          setProviderInvoice(invoices);
          setTimeout(() => {
            modelRef.current?.refetch()
          });
        } finally {
          setIsLoading(false)
        }
      }
      run(providerInvoiceId);
      setIsLoading(true)
    }
  }, [providerInvoiceId]);

  const deleteRecord = async (id: string) => {
    const { errors } = await ProviderNoteApi.delete(id);
    if (errors?.length) {
      showAlert('danger', 'La Nota de Credito/Debito no puedo ser eliminado');
    } else {
      showAlert('success', 'La Nota de Credito/Debito fue eliminado correctamente');
    }
    modelRef.current?.refetch();
  }

  const getInvoiceNotes = async (): Promise<FetchResponse<ProviderNoteDataType>> => {
    return {
      items: providerInvoice.providerNotes || []
    }
  }

  return (
    <PageContainer
      title="Lista Nota de Credito/Debito de Factura"
      backUrl="/user/provider-invoices"
      primaryActions={[
        {
          onClick: () => {
            navigate(`/user/provider-invoices/notes/add/${providerInvoiceId}`)
          },
          actionTitle: "Nuevo",
          color: "success"
        }
      ]}
    >
      <Container fluid>
        <Row>
          <Col xs="2" md="2">
            <h5><b>Provedor:</b></h5>
          </Col>
          <Col>
            <h5><b>{providerInvoice?.provider?.nombre}</b></h5>
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Fecha:</b>
          </Col>
          <Col>
            {formatDate(providerInvoice?.fecha)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Numero Factura:</b>
          </Col>
          <Col>
            {providerInvoice?.numero}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Subtotal:</b>
          </Col>
          <Col>
            {formatMoneyValue(providerInvoice?.subtotal, providerInvoice?.currency?.currency_id)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Descuento:</b>
          </Col>
          <Col>
            {formatMoneyValue(providerInvoice?.descuento, providerInvoice?.currency?.currency_id)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Impuestos:</b>
          </Col>
          <Col>
            {formatMoneyValue(providerInvoice?.impuestos, providerInvoice?.currency?.currency_id)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <h5><b>Total:</b></h5>
          </Col>
          <Col>
            <h5><b>{formatMoneyValue(providerInvoice?.total, providerInvoice?.currency?.currency_id)}</b></h5>
          </Col>
        </Row>
        <hr />
      </Container>
      <ModelIndex<ProviderNoteDataType>
        ref={modelRef}
        fetchData={() => getInvoiceNotes()}
        primaryProperties={[
          {
            key: "sequence",
            highlighted: true,
            className: styles.sequence
          },
          {
            key: (d) => d.numero,
            alignment: "Start"
          },
        ]}
        primaryPropertiesSizing={
          [
            { sm: "4", xs: "12" },
            { sm: "8", xs: "12" }
          ]
        }
        secondaryProperties={[
          {
            key: (data) => {
              return formatDate(data.fecha)
            },
            title: "Fecha",
          },
          {
            key: (data) => {
              return PROVIDER_NOTE_TYPES[`${data.tipo}`]// ? 'Pagada' : 'Pendiente'
            },
          },
          {
            key: (data) => {
              return formatMoneyValue(data.total, data.currency.currency_id)
            },
            title: "Total",
            highlighted: true
          }
        ]}
        secondaryPropertiesSizing={
          [
            { sm: "3", md: "2" },
            { sm: "3", md: "2" },
            { sm: "3", md: "2" },
            { sm: "3", md: "2" }
          ]
        }
        listActions={[
          {
            isVisible: (data) => !!data.atvMessages && data.atvMessages?.length > 0,
            href: "/user/atv-messages/listByInvoiceNote/{:id}",
            actionTitle: "Ver Mensajes de ATV",
            iconProps: { icon: faPaperPlane }
          },
          {
            href: `/user/provider-invoices/notes/edit/{:id}/${providerInvoiceId}`,
            actionTitle: "Editar Nota",
            iconProps: { icon: faEdit }
          },
          {
            actionTitle: "Eliminar Nota",
            iconProps: { icon: faTrash },
            needConfirm: true,
            confirmProps: {
              title: "Eliminar Nota",
              confirmMessage: "¿Seguro que desea eliminar esta Nota?",
              confirmBtnText: "Eliminar"
            },
            onClick: (id: string) => { deleteRecord(id) }
          }
        ]}
      >
      </ModelIndex>
      <LoadingScreen isLoading={isLoading} />
    </PageContainer >
  );
}

export default ProviderNoteIndex;
